import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import L from 'leaflet';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Bed, 
  Bath, 
  MapPin, 
  ArrowLeft, 
  Heart, 
  Send, 
  User,
  Calendar,
  Home,
  CheckCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Train,
  Bus,
  TramFront,
  ShoppingCart,
  GraduationCap,
  School,
  Sparkles
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  readListingAmenityCoords,
  readListingAmenities,
  readListingDraft,
  readListingStreetViewState,
  cacheListingAmenityCoords,
} from '../lib/listingCache';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TRANSPORT_ICON = { tube: TramFront, tram: TramFront, train: Train, bus: Bus };
const SCHOOL_ICON = { nursery: School, primary: School, school: GraduationCap };
const WALKING_METERS_PER_MINUTE = 80;

function listingStreetViewUrl(property) {
  if (!property?.id || !property?.listing_street_view_image) return null;
  return `${API_URL}/uploads/listing-images/${property.id}/${property.listing_street_view_image}`;
}

function metersToWalkingMinutes(distanceMeters) {
  if (distanceMeters == null) return null;
  return Math.max(1, Math.round(Number(distanceMeters) / WALKING_METERS_PER_MINUTE));
}

function walkingMinutesToMeters(minutes) {
  return Math.max(0, Math.round(Number(minutes) * WALKING_METERS_PER_MINUTE));
}

function staticMapRemoteUrl(lat, lng, zoom = 16, size = '1200x400') {
  return `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=${zoom}&size=${size}&maptype=mapnik&markers=${lat},${lng},red-pushpin`;
}

const COLOURS = {
  transport: '#1E90FF',
  supermarkets: '#0F766E',
  schools: '#FFB300',
  leisure: '#FF3B30',
  parks: '#65A30D',
};

const PROPERTY_COLOUR = '#4F46E5';

function _makeSvgDataUrl(fillColor, size = 14, strokeColor = '#fff', strokeWidth = 2, shape = 'circle') {
  if (shape === 'pin') {
    const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 24 24'><path fill='${fillColor}' d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z'/><circle cx='12' cy='9' r='2.5' fill='${strokeColor}'/></svg>`;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  }
  const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'><circle cx='${size / 2}' cy='${size / 2}' r='${Math.max(0, (size - strokeWidth) / 2)}' fill='${fillColor}' stroke='${strokeColor}' stroke-width='${strokeWidth}'/></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

function AmenityOverlay({ entries = [] }) {
  const map = useMap();
  const entriesKey = JSON.stringify(entries);

  useEffect(() => {
    if (!map || !map.getPanes) return;
    const panes = map.getPanes();
    const hostPane = panes.markerPane || map.getContainer();
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '0';
    container.style.top = '0';
    container.style.pointerEvents = 'none';
    hostPane.appendChild(container);

    const nodes = new Map();

    const update = () => {
      if (!map || !container) return;
      const toKeep = new Set();
      for (const place of entries) {
        if (place.lat == null || place.lng == null) continue;
        const key = `${place.cat}::${place.name}::${place.lat}::${place.lng}`;
        toKeep.add(key);
        let node = nodes.get(key);
        if (!node) {
          node = document.createElement('div');
          node.style.position = 'absolute';
          node.style.pointerEvents = 'auto';
          node.style.transform = 'translate(-50%, -100%)';
          node.style.zIndex = '400';
          const colour = COLOURS[place.cat] || '#888';
          const size = 36;
          const img = document.createElement('img');
          img.src = _makeSvgDataUrl(colour, size, '#fff', 1.5, 'pin');
          img.width = size;
          img.height = size;
          img.style.display = 'block';
          img.style.userSelect = 'none';
          img.style.background = 'transparent';
          node.appendChild(img);
          node.title = place.name || '';
          node.onclick = () => { try { map.flyTo([Number(place.lat), Number(place.lng)], Math.max(map.getZoom(), 16)); } catch (e) {} };
          container.appendChild(node);
          nodes.set(key, node);
        }
        try {
          const p = map.latLngToLayerPoint([Number(place.lat), Number(place.lng)]);
          node.style.left = `${Math.round(p.x)}px`;
          node.style.top = `${Math.round(p.y)}px`;
        } catch (e) {}
      }

      for (const [key, node] of nodes.entries()) {
        if (!toKeep.has(key)) {
          try { if (node.parentNode) node.parentNode.removeChild(node); } catch (e) {}
          nodes.delete(key);
        }
      }
    };

    update();
    map.on('move zoom zoomend resize viewreset', update);

    return () => {
      try { map.off('move zoom zoomend resize viewreset', update); } catch (e) {}
      try { if (container.parentNode) container.parentNode.removeChild(container); } catch (e) {}
      nodes.clear();
    };
  }, [map, entriesKey, entries]);

  return null;
}

function PropertyOverlay({ lat, lng, radius }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;

    const circle = L.circle([lat, lng], {
      radius,
      color: '#6366F1',
      fillColor: '#6366F1',
      fillOpacity: 0.07,
      opacity: 0.5,
      weight: 1.5,
      interactive: false,
    }).addTo(map);

    const markerPane = map.getPanes().markerPane;
    const pin = document.createElement('div');
    pin.style.position = 'absolute';
    pin.style.pointerEvents = 'none';
    pin.style.transform = 'translate(-50%, -100%)';
    pin.style.zIndex = '500';
    const pinSize = 40;
    const pinImg = document.createElement('img');
    pinImg.src = _makeSvgDataUrl(PROPERTY_COLOUR, pinSize, '#fff', 2, 'pin');
    pinImg.width = pinSize;
    pinImg.height = pinSize;
    pinImg.style.display = 'block';
    pinImg.style.background = 'transparent';
    pin.appendChild(pinImg);
    markerPane.appendChild(pin);

    const updatePin = () => {
      const p = map.latLngToLayerPoint([lat, lng]);
      pin.style.left = `${Math.round(p.x)}px`;
      pin.style.top = `${Math.round(p.y)}px`;
    };

    updatePin();
    map.on('move zoom zoomend resize viewreset', updatePin);

    return () => {
      try { map.off('move zoom zoomend resize viewreset', updatePin); } catch (e) {}
      try { circle.remove(); } catch (e) {}
      try { if (pin.parentNode) pin.parentNode.removeChild(pin); } catch (e) {}
    };
  }, [map, lat, lng, radius]);

  return null;
}

const FEATURE_OPTIONS = {
  parking: 'Parking',
  garden: 'Garden',
  balcony: 'Balcony',
  pets_allowed: 'Pets Allowed',
  bills_included: 'Bills Included',
  double_glazing: 'Double Glazing',
  central_heating: 'Central Heating',
  fitted_kitchen: 'Fitted Kitchen',
  near_transport: 'Near Transport',
  near_schools: 'Near Schools',
  new_build: 'New Build',
  wheelchair_accessible: 'Wheelchair Access',
};

function normaliseAmenities(rawAmenities) {
  if (!rawAmenities) return {};
  if (typeof rawAmenities === 'string') {
    try {
      const parsed = JSON.parse(rawAmenities);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof rawAmenities === 'object' ? rawAmenities : {};
}

function mergeAmenitySources(...sources) {
  const keys = ['transport', 'supermarkets', 'schools', 'leisure', 'parks', 'custom'];
  return keys.reduce((acc, key) => {
    for (const source of sources) {
      if (Array.isArray(source?.[key]) && source[key].length > 0) {
        acc[key] = source[key];
        break;
      }
    }
    if (!acc[key]) acc[key] = [];
    return acc;
  }, {});
}

export const PropertyDetail = () => {
  useEffect(() => { document.title = 'Property Details - SIYAK'; }, []);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isLandlord, isTenant, isAuthenticated } = useAuth();
  const initialStreetViewState = readListingStreetViewState(id);
  const isUserTenant = isTenant();
  const isUserLandlord = isLandlord();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  
  // Application form
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyMessage, setApplyMessage] = useState('');
  const [withdrawConfirmOpen, setWithdrawConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Street View / Carousel states (ported from ListingBuilder)
  const [svError, setSvError] = useState(() => initialStreetViewState.error);
  const [svHidden, setSvHidden] = useState(() => initialStreetViewState.hidden);
  const [svDataUrl, setSvDataUrl] = useState(() => initialStreetViewState.dataUrl);
  const [slideOrder, setSlideOrder] = useState(null);
  const [heroIndex, setHeroIndex] = useState(0);
  const [imageError, setImageError] = useState(null);
  const [amenityRadius, setAmenityRadius] = useState(10);
  const [amenityCoords, setAmenityCoords] = useState(null);
  const [amenitiesView, setAmenitiesView] = useState('list');
  const [showInteractiveMap, setShowInteractiveMap] = useState(true);
  const [staticMapError, setStaticMapError] = useState(false);
  const [cachedAmenities, setCachedAmenities] = useState({});
  const [cachedExtras, setCachedExtras] = useState([]);

  // Generate default message based on user profile
  const getDefaultMessage = () => {
    if (!user?.email) return '';
    const name = user?.name || 'there';
    const email = user?.email || '';
    const phone = user?.phone || '';
    const occupation = user?.occupation || 'a professional';
    
    let message = `Hi, my name is ${name}. I'm interested in this property. I work as ${occupation} and have a stable income. I would appreciate the opportunity to discuss this further.\n\nPlease feel free to contact me at ${email}`;
    if (phone) message += ` or ${phone}`;
    message += ' for further details. Thank you!';
    return message;
  };

  // Set default message when Apply dialog opens
  const handleApplyDialogOpen = (open) => {
    if (open && applyMessage === '') {
      setApplyMessage(getDefaultMessage());
    }
    setApplyOpen(open);
  };

  const fetchProperty = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Failed to fetch property:', error);
      toast.error('Property not found');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const checkSavedStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/saved-properties`);
      const saved = response.data.some(s => s.property_id === id);
      setIsSaved(saved);
    } catch (error) {
      console.error('Failed to check saved status:', error);
    }
  }, [id]);

  const checkApplicationStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/applications`);
      const applied = response.data.some(a => a.property_id === id);
      setHasApplied(applied);
    } catch (error) {
      console.error('Failed to check application status:', error);
    }
  }, [id]);

  useEffect(() => {
    fetchProperty();
    if (isUserTenant) {
      checkSavedStatus();
      checkApplicationStatus();
    }
  }, [fetchProperty, checkSavedStatus, checkApplicationStatus, isUserTenant]);

  // Try to use locally cached Street View if available
  useEffect(() => {
    if (!property?.id || svError || svHidden) return;
    const storedStreetViewUrl = listingStreetViewUrl(property);
    if (storedStreetViewUrl) {
      setSvDataUrl(storedStreetViewUrl);
      return;
    }

    const cachedStreetView = readListingStreetViewState(property.id);
    if (cachedStreetView.hidden) {
      setSvHidden(true);
    } else if (cachedStreetView.error) {
      setSvError(true);
    } else if (cachedStreetView.dataUrl) {
      setSvDataUrl(cachedStreetView.dataUrl);
    } else {
      setSvHidden(true);
    }
  }, [property, svError, svHidden]);

  // Keep slideOrder in sync with available slides (map + uploaded images).
  useEffect(() => {
    const hasMap = !svError && !svHidden && !!svDataUrl;
    const listingImages = Array.isArray(property?.listing_images) ? property.listing_images : [];
    const fallbackImages = Array.isArray(property?.images) ? property.images : [];
    // Only use fallback images if listingImages is totally empty, else use listingImages.
    const hasListingImages = property?.listing_images && property.listing_images.length > 0;
    const imagesToUse = hasListingImages ? property.listing_images : fallbackImages;

    setSlideOrder((prev) => {
      // In PropertyDetail, the "id" in slideOrder for uploaded images is their URL, so we map them correctly later
      const all = [...(hasMap ? ['sv'] : []), ...imagesToUse];
      if (prev === null) return all;
      const valid = prev.filter((id) => all.includes(id));
      const added = all.filter((id) => !valid.includes(id));
      return [...valid, ...added];
    });
  }, [svError, svHidden, svDataUrl, property]);

  useEffect(() => {
    if (!property) return;
    if (property.lat != null && property.lng != null && !amenityCoords) {
      setAmenityCoords({ lat: property.lat, lng: property.lng });
    }
  }, [property, amenityCoords]);

  useEffect(() => {
    if (!property || amenityCoords) return;
    if (!property.postcode) return;

    const cachedCoords = readListingAmenityCoords(property.id, property);
    if (cachedCoords?.lat != null && cachedCoords?.lng != null) {
      setAmenityCoords(cachedCoords);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(property.postcode)}`);
        if (!res.ok) return;
        const data = await res.json();
        const lat = data?.result?.latitude;
        const lng = data?.result?.longitude;
        if (!cancelled && lat != null && lng != null) {
          cacheListingAmenityCoords(property.id, property.postcode, { lat, lng });
          setAmenityCoords({ lat, lng });
        }
      } catch (e) {}
    })();

    return () => { cancelled = true; };
  }, [property, amenityCoords]);

  useEffect(() => {
    if (!property?.id) return;
    const draft = readListingDraft(property.id);
    const nextAmenities = readListingAmenities(property.id, property, 2000);
    setCachedExtras(draft.customAmenities || []);
    setCachedAmenities(nextAmenities);
  }, [property]);

  const handleSave = async () => {
    if (!isAuthenticated()) {
      toast.error('Please login to save properties');
      return;
    }
    if (!isTenant()) {
      toast.error('Only tenants can save properties');
      return;
    }

    try {
      if (isSaved) {
        await axios.delete(`${API_URL}/api/saved-properties/${id}`);
        setIsSaved(false);
        toast.success('Property removed from saved');
      } else {
        await axios.post(`${API_URL}/api/saved-properties`, { property_id: id });
        setIsSaved(true);
        toast.success('Property saved');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!isAuthenticated()) {
      toast.error('Please login to apply');
      return;
    }
    if (!isTenant()) {
      toast.error('Only tenants can apply for properties');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/applications`, {
        property_id: id,
        message: applyMessage
      });
      toast.success('Application submitted successfully!');
      setApplyOpen(false);
      setHasApplied(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleWithdrawApplication = async () => {
    setSubmitting(true);
    try {
      const response = await axios.get(`${API_URL}/api/applications`);
      const application = response.data.find(a => a.property_id === id);
      
      if (application) {
        await axios.delete(`${API_URL}/api/applications/${application.id}`);
        setHasApplied(false);
        toast.success('Application withdrawn');
        setWithdrawConfirmOpen(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to withdraw application');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
        </div>
      </Layout>
    );
  }

  if (!property) return null;

  const isAvailable = property.is_publicly_available === true;
  const displayStatus = property.is_occupied ? 'occupied' : (property.status || 'available');
  const availabilityLabel = property.is_occupied
    ? 'Occupied'
    : isAvailable
      ? 'Available'
      : 'Unavailable';
  const availabilitySubtext = property.is_occupied
    ? 'Currently let'
    : isAvailable
      ? 'Now'
      : 'Not accepting applications';

  const svUrl = (!svError && !svHidden && svDataUrl) ? svDataUrl : null;
  // Build ordered slides from the drag-managed slideOrder state seamlessly
  const orderedSlides = (slideOrder || []).map((id) => {
    if (id === 'sv') return svUrl ? { type: 'sv', src: svUrl, id: 'sv' } : null;
    
    // In property detail, id is the filename or the full string (for fallbacks)
    let src;
    if (property.listing_images && property.listing_images.length > 0) {
      src = `${API_URL}/uploads/listing-images/${property.id}/${id}`;
    } else {
      src = id; // use as direct fallback URL if it's the fallback array
    }
    return { type: 'img', src, filename: id, id };
  }).filter(Boolean);

  const boundedHeroIndex = Math.max(0, Math.min(heroIndex, orderedSlides.length - 1));
  const heroSrc = orderedSlides[boundedHeroIndex]?.src ?? null;

  const amenities = mergeAmenitySources(
    { custom: cachedExtras },
    normaliseAmenities(cachedAmenities),
    normaliseAmenities(property.listing_amenities)
  );
  const amenityRadiusMeters = walkingMinutesToMeters(amenityRadius);
  const amenitySections = [
    { key: 'transport', label: 'Transport', Icon: Train },
    { key: 'supermarkets', label: 'Supermarkets', Icon: ShoppingCart },
    { key: 'schools', label: 'Schools', Icon: GraduationCap },
    { key: 'leisure', label: 'Leisure', Icon: Sparkles },
    { key: 'parks', label: 'Parks', Icon: MapPin },
    { key: 'custom', label: 'Extras', Icon: Sparkles },
  ];
  const hasAmenitySource = Boolean(
    property.postcode ||
    property.lat != null ||
    property.lng != null ||
    amenitySections.some(({ key }) => Array.isArray(amenities[key]))
  );
  const mapEntries = ['transport', 'supermarkets', 'schools', 'leisure', 'parks']
    .flatMap((cat) => (amenities[cat] || []).map((place) => ({ ...place, cat })))
    .filter((place) => place.distance == null || place.distance <= amenityRadiusMeters);

  const statusColors = {
    available: 'bg-green-100 text-green-700',
    occupied: 'bg-slate-100 text-slate-700',
    maintenance: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <Layout>
      <div className="bg-navy-50 min-h-screen" data-testid="property-detail-page">
        {/* Back Button */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-muted-foreground"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to listings
          </Button>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image Gallery (Same layout as ListingBuilder API) */}
              <Card className="overflow-hidden p-0 border-0 shadow-none bg-transparent">
                <div className="relative w-full h-72 md:h-96 rounded-2xl overflow-hidden bg-navy-100 mb-0 shadow-sm">
                  {heroSrc ? (
                    <>
                      <img
                        src={heroSrc}
                        alt="Listing hero"
                        className="w-full h-full object-cover"
                        onError={() => {
                          const current = orderedSlides[boundedHeroIndex];
                          if (!current) return;
                          if (current.type === 'sv') {
                            setSvError(true);
                          }
                          else setImageError('Could not load image');
                        }}
                      />

                      {/* Carousel controls when more than one slide exists */}
                      {orderedSlides.length > 1 && (
                        <>
                          <button
                            type="button"
                            onClick={() => setHeroIndex((i) => (i - 1 + orderedSlides.length) % orderedSlides.length)}
                            className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-9 h-9 flex items-center justify-center"
                            aria-label="Previous image"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setHeroIndex((i) => (i + 1) % orderedSlides.length)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white rounded-full w-9 h-9 flex items-center justify-center"
                            aria-label="Next image"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="w-full h-full bg-navy-100 flex flex-col items-center justify-center gap-2">
                      <MapPin className="w-10 h-10 text-navy-400" />
                      <span className="text-navy-400 text-sm">Image unavailable</span>
                    </div>
                  )}
                  {/* Gradient overlay (non-interactive so buttons remain clickable) */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent pointer-events-none" />
                  
                  {/* Price overlay */}
                  <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between">
                    <div>
                      <p className="text-white text-3xl font-bold leading-none drop-shadow">
                        £{Number(property.rent ?? 0).toLocaleString('en-GB')}
                        <span className="text-lg font-normal ml-1 opacity-90">pcm</span>
                      </p>
                      <p className="text-white/80 text-sm mt-1 flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {property.address
                          ? (property.postcode && property.address.includes(property.postcode)
                              ? property.address
                              : `${property.address}${property.postcode ? `, ${property.postcode}` : ''}`)
                          : property.postcode}
                      </p>
                    </div>
                    <span className="bg-white/20 backdrop-blur-sm text-white text-sm px-3 py-1.5 rounded-full border border-white/30 capitalize">
                      {property.property_type || property.type || 'Property'}
                    </span>
                  </div>
                </div>
              </Card>

              {/* Property Info */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className={statusColors[displayStatus] || statusColors.available}>
                          {displayStatus}
                        </Badge>
                        <Badge variant="secondary" className="capitalize">
                          {property.property_type}
                        </Badge>
                      </div>
                      <h1 className="font-heading text-2xl md:text-3xl font-bold text-navy-900">
                        {property.title}
                      </h1>
                    </div>
                    <div className="text-right">
                      <div className="text-3xl font-heading font-bold text-navy-900">
                        £{property.rent?.toLocaleString()}
                      </div>
                      <div className="text-muted-foreground">per month</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-muted-foreground mb-6">
                    <MapPin className="h-5 w-5" />
                    <span>{property.address}</span>
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-6 border-y">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-royal-50">
                        <Bed className="h-5 w-5 text-royal-600" />
                      </div>
                      <div>
                        <div className="font-semibold">{property.bedrooms}</div>
                        <div className="text-sm text-muted-foreground">Bedrooms</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-royal-50">
                        <Bath className="h-5 w-5 text-royal-600" />
                      </div>
                      <div>
                        <div className="font-semibold">{property.bathrooms}</div>
                        <div className="text-sm text-muted-foreground">Bathrooms</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-royal-50">
                        <Home className="h-5 w-5 text-royal-600" />
                      </div>
                      <div>
                        <div className="font-semibold capitalize">{property.property_type}</div>
                        <div className="text-sm text-muted-foreground">Type</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-royal-50">
                        <Calendar className="h-5 w-5 text-royal-600" />
                      </div>
                      <div>
                        <div className="font-semibold">{availabilityLabel}</div>
                        <div className="text-sm text-muted-foreground">{availabilitySubtext}</div>
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="mt-6">
                    <h2 className="font-heading font-semibold text-lg mb-3">Description</h2>
                    <p className="text-muted-foreground whitespace-pre-line">
                      {property.description}
                    </p>
                  </div>

                  {/* Features List */}
                  {property.features?.length > 0 && (
                    <div className="mt-6">
                      <h2 className="font-heading font-semibold text-lg mb-3">Features</h2>
                      <div className="grid grid-cols-2 gap-2">
                        {property.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            {FEATURE_OPTIONS[feature] || feature.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAmenitySource && (
                    <div className="mt-6 bg-white rounded-xl border border-border p-5 shadow-sm">
                      <div className="flex flex-col gap-2 mb-4">
                        <div className="flex items-center gap-2">
                          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                            Nearby Amenities
                          </h2>
                        </div>
                        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3 sm:gap-y-2">
                          <div className="grid w-full min-w-0 grid-cols-[auto,1fr,auto] items-center gap-2 sm:flex sm:flex-1 sm:items-center">
                            <label className="text-xs text-muted-foreground shrink-0">Radius:</label>
                            <input
                              type="range"
                              min={2}
                              max={20}
                              step={1}
                              value={amenityRadius}
                              onChange={(e) => setAmenityRadius(Number(e.target.value))}
                              className="flex-1 min-w-0"
                            />
                            <span className="text-xs text-right text-navy-600 whitespace-nowrap sm:w-20 sm:shrink-0">{amenityRadius} min walk</span>
                          </div>
                          <div className="flex w-full items-center bg-navy-100 rounded-lg p-0.5 gap-0.5 sm:w-auto sm:shrink-0">
                            {['list', 'map'].map((view) => (
                              <button
                                key={view}
                                type="button"
                                onClick={() => setAmenitiesView(view)}
                                className={`flex-1 text-xs px-2.5 py-1 rounded-md capitalize transition-colors sm:flex-none ${
                                  amenitiesView === view
                                    ? 'bg-white text-navy-900 shadow-sm font-medium'
                                    : 'text-navy-500 hover:text-navy-700'
                                }`}
                              >
                                {view}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      {amenitiesView === 'map' && (
                        <div className="mb-4">
                          <div className="w-full h-64 rounded-lg overflow-hidden border border-border relative">
                            {amenityCoords ? (
                              !showInteractiveMap ? (
                                staticMapError ? (
                                  <div className="w-full h-64 bg-navy-50 flex items-center justify-center flex-col gap-2">
                                    <MapPin className="w-10 h-10 text-navy-400" />
                                    <div className="text-sm text-navy-500">Interactive map available</div>
                                    <button
                                      type="button"
                                      onClick={() => setShowInteractiveMap(true)}
                                      className="text-xs px-2 py-1 rounded bg-white border border-border text-navy-700 shadow-sm"
                                    >
                                      Load interactive map
                                    </button>
                                  </div>
                                ) : (
                                  <>
                                    <img
                                      src={staticMapRemoteUrl(amenityCoords.lat, amenityCoords.lng, 15, '900x300')}
                                      alt="Map"
                                      className="w-full h-64 object-cover"
                                      onError={(e) => {
                                        e.currentTarget.onerror = null;
                                        setStaticMapError(true);
                                      }}
                                    />
                                    <div className="absolute top-2 right-2">
                                      <button
                                        type="button"
                                        onClick={() => setShowInteractiveMap(true)}
                                        className="text-xs px-2 py-1 rounded bg-white border border-border text-navy-700 shadow-sm"
                                      >
                                        Load interactive map
                                      </button>
                                    </div>
                                  </>
                                )
                              ) : (
                                <MapContainer
                                  center={[Number(amenityCoords.lat), Number(amenityCoords.lng)]}
                                  zoom={15}
                                  scrollWheelZoom={false}
                                  className="w-full h-64"
                                  zoomControl={true}
                                  attributionControl={true}
                                >
                                  <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                  />
                                  <PropertyOverlay lat={Number(amenityCoords.lat)} lng={Number(amenityCoords.lng)} radius={amenityRadiusMeters} />
                                  <AmenityOverlay entries={mapEntries} />
                                </MapContainer>
                              )
                            ) : (
                              <div className="w-full h-64 flex items-center justify-center text-sm text-muted-foreground">Map unavailable</div>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-3 mt-2">
                            {[
                              { label: 'Transport', color: COLOURS.transport },
                              { label: 'Supermarkets', color: COLOURS.supermarkets },
                              { label: 'Schools', color: COLOURS.schools },
                              { label: 'Leisure', color: COLOURS.leisure },
                              { label: 'Parks', color: COLOURS.parks },
                              { label: 'Property', color: PROPERTY_COLOUR },
                            ].map(({ label, color }) => (
                              <span key={label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
                                {label}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {amenitiesView === 'list' && (
                        <div className="divide-y divide-border">
                          {amenitySections.map(({ key, label, Icon }) => (
                            <div key={key} className="py-3 first:pt-0 last:pb-0">
                              <p className="text-xs font-semibold text-navy-600 mb-2 flex items-center gap-1.5">
                                <Icon className="w-3.5 h-3.5" />
                                {label}
                              </p>
                              {(() => {
                                const visible = (amenities[key] || [])
                                  .filter((place) => place.distance == null || place.distance <= amenityRadiusMeters);
                                if (visible.length === 0) {
                                  return <p className="text-sm text-muted-foreground italic">None found nearby</p>;
                                }
                                return (
                                  <ul className="space-y-1.5">
                                    {visible.map((place, index) => {
                                      const PlaceIcon = key === 'transport'
                                        ? (TRANSPORT_ICON[place.kind] ?? Train)
                                        : key === 'schools'
                                          ? (SCHOOL_ICON[place.kind] ?? GraduationCap)
                                          : key === 'supermarkets'
                                            ? ShoppingCart
                                            : key === 'leisure'
                                              ? Sparkles
                                              : key === 'parks'
                                                ? MapPin
                                                : null;
                                      return (
                                        <li key={`${key}-${index}-${place.name || 'amenity'}`} className="flex items-center justify-between gap-2 min-w-0">
                                          <span className="text-sm text-navy-700 flex items-center gap-1.5 min-w-0">
                                            {PlaceIcon && <PlaceIcon className="w-3.5 h-3.5 text-navy-400 shrink-0" />}
                                            <span className="truncate">{place.name || 'Nearby amenity'}</span>
                                          </span>
                                          <span className="text-xs text-muted-foreground shrink-0 flex items-center gap-2">
                                            {place.distance != null && `${metersToWalkingMinutes(place.distance)} min walk`}
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                );
                              })()}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Actions Card */}
              <Card>
                <CardHeader>
                  <CardTitle>Interested in this property?</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {isUserLandlord && (
                    <>
                      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        Preview mode — this page shows how tenants see the listing. Tenant actions are disabled for this preview.
                      </div>
                      <Button className="w-full" disabled>
                        <Send className="h-4 w-4 mr-2" />
                        Apply Now
                      </Button>
                      <Button variant="ghost" className="w-full" disabled>
                        <Heart className="h-4 w-4 mr-2" />
                        Save Property
                      </Button>
                    </>
                  )}

                  {!isAuthenticated() && (
                    <>
                      <Button asChild className="w-full" data-testid="login-to-apply-btn">
                        <Link to={`/login?from=${encodeURIComponent(`/properties/${id}`)}`}>
                          <Send className="h-4 w-4 mr-2" />
                          Login to Apply
                        </Link>
                      </Button>
                      <Button asChild variant="outline" className="w-full" data-testid="register-to-apply-btn">
                        <Link to={`/register?from=${encodeURIComponent(`/properties/${id}`)}&role=tenant`}>
                          Create Tenant Account
                        </Link>
                      </Button>
                    </>
                  )}

                  {/* Apply Button */}
                  {isTenant() && isAvailable && (
                    <>
                      {hasApplied ? (
                        <Button
                          variant="destructive"
                          className="w-full"
                          onClick={() => setWithdrawConfirmOpen(true)}
                          disabled={submitting}
                          data-testid="withdraw-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Withdraw Application
                        </Button>
                      ) : (
                        <Dialog open={applyOpen} onOpenChange={handleApplyDialogOpen}>
                          <DialogTrigger asChild>
                            <Button 
                              className="w-full" 
                              data-testid="apply-btn"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Apply Now
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Apply for {property.title}</DialogTitle>
                          <DialogDescription>
                            Submit your application for this property. The landlord will review and contact you.
                          </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={handleApply} className="space-y-4">
                          <div>
                            <Label>Your Message *</Label>
                            <Textarea
                              required
                              placeholder="Tell the landlord about yourself..."
                              value={applyMessage}
                              onChange={(e) => setApplyMessage(e.target.value)}
                              rows={4}
                              data-testid="apply-message-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                              💡 Your message has been pre-filled with your profile information. Feel free to customize it.
                            </p>
                          </div>
                          <Button type="submit" className="w-full" disabled={submitting || !applyMessage.trim()}>
                            {submitting ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Submitting...
                              </>
                            ) : (
                              'Submit Application'
                            )}
                          </Button>
                        </form>
                      </DialogContent>
                        </Dialog>
                      )}
                    </>
                  )}



                  {/* Save Button */}
                  {isTenant() && (
                    <Button
                      variant="ghost"
                      className="w-full"
                      onClick={handleSave}
                      data-testid="save-property-btn"
                    >
                      <Heart className={`h-4 w-4 mr-2 ${isSaved ? 'fill-red-500 text-red-500' : ''}`} />
                      {isSaved ? 'Saved' : 'Save Property'}
                    </Button>
                  )}

                  {isAuthenticated() && !isTenant() && !isUserLandlord && (
                    <p className="text-sm text-center text-muted-foreground">
                      Applications are available for tenant accounts.
                    </p>
                  )}

                  {isTenant() && !isAvailable && (
                    <p className="text-sm text-center text-muted-foreground">
                      This property is not currently accepting applications.
                    </p>
                  )}

                  {!isAuthenticated() && (
                    <p className="text-sm text-center text-muted-foreground">
                      <Link to="/login" className="text-royal-600 hover:underline">Login</Link> to save or apply
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Landlord Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Listed by</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-royal-100 flex items-center justify-center">
                      <User className="h-6 w-6 text-royal-600" />
                    </div>
                    <div>
                      <div className="font-semibold">{property.landlord_name}</div>
                      <div className="text-sm text-muted-foreground">Property Owner</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <AlertDialog open={withdrawConfirmOpen} onOpenChange={setWithdrawConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Withdraw Application?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to withdraw your application for {property?.title}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex justify-end gap-3">
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleWithdrawApplication} disabled={submitting} className="bg-destructive hover:bg-destructive/90">
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Withdrawing...
                </>
              ) : (
                'Withdraw'
              )}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
};
